import { useMemo } from "react";
import {
  PRODUCT_OPTIONS,
  type ProductOption,
  type ProductValue,
} from "@/views/dashboard/header/components/productOptions";
import { useWorkforceGate } from "@/views/workforce/hooks/useWorkforceGate";
import { useEmployeeGate } from "@/hooks/useEmployeeGate";

export interface ResolvedProductOption extends ProductOption {
  /** May this account open the product? */
  enabled: boolean;
}

export interface ProductOptionsResult {
  /** Every product, each with its gate resolved. Order is display order. */
  options: ResolvedProductOption[];
  /** Only the products this account may open. */
  enabledOptions: ResolvedProductOption[];
  isLoading: boolean;
}

/**
 * Resolves every product's own gate in one place, so the header switcher and
 * the App Preferences page cannot disagree about what a user may open.
 *
 * Each product keeps its OWN gate rather than sharing one rule:
 *   service-desk — always on; disabling it would lock the instance out
 *   workforce    — useWorkforceGate (admin-only, build-time const) — unchanged
 *   employee     — useEmployeeGate (per-instance toggle, skypoint_admin bypass)
 *
 * Adding a fourth product means one PRODUCT_OPTIONS entry and one case here.
 */
export function useProductOptions(): ProductOptionsResult {
  const workforce = useWorkforceGate();
  const employee = useEmployeeGate();

  const isLoading = workforce.isLoading || employee.isLoading;

  const options = useMemo(() => {
    const isEnabled = (value: ProductValue): boolean => {
      switch (value) {
        case "workforce":
          return workforce.enabled;
        case "employee":
          return employee.enabled;
        case "service-desk":
        default:
          return true;
      }
    };
    return PRODUCT_OPTIONS.map((option) => ({ ...option, enabled: isEnabled(option.value) }));
  }, [workforce.enabled, employee.enabled]);

  const enabledOptions = useMemo(() => options.filter((option) => option.enabled), [options]);

  return { options, enabledOptions, isLoading };
}
